import React, { useEffect, useState, useRef } from 'react';
import '../styles.css'
import getAPIbaseURL from "../../../utils/helpers";
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchUsers } from '../../../redux/slices/userSlice';
import {
    Card,
    Container,
    Row,
    Col,
    Image,
    Badge
} from "react-bootstrap";

import { FaTrash } from 'react-icons/fa';

import { updateNotification, deleteNotification, fetchNotifications } from '../../../redux/slices/notificationsSlice';
import useSocketConnect from '../../../hooks/useSocket';
import HeaderMobile from "../../reusableComps/HeaderMobile/HeaderMobile";

import PauloPfp from '../../../assets/icons/PauloPfp.svg';
import TomasPfp from '../../../assets/icons/TomasPfp.svg';
import CoolDog from '../../../assets/icons/CoolDogPfp.svg';
import CochePfp from '../../../assets/icons/Coche.svg';
import Carrings from '../../../assets/icons/CarringsPfp.svg';

const defaultPictures = [PauloPfp, TomasPfp, CoolDog, CochePfp, Carrings];

const Notifications = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const containerRef = useRef(null);

    const notifications = useSelector((state) => state.notifications.items);
    const users = useSelector((state) => state.users.items);
    const user = useSelector((state) => state.auth.user);
    const socket = useSocketConnect();

    const [lastClickedId, setLastClickedId] = useState(null);
    const [clickCount, setClickCount] = useState(0);

    useEffect(() => {
        if (!users || users.length === 0) {
            dispatch(fetchUsers());
        }
    }, [dispatch, users]);

    useEffect(() => {
        if (user?.id) {
            dispatch(fetchNotifications({ author_id: user.id }));
        }
    }, [dispatch, user]);

    useEffect(() => {
        if (!socket || !user?.id) return;

        const eventName = `user_${user.id}`;
        const handleNotification = (_data) => {
            dispatch(fetchNotifications({ author_id: user.id }));
        };

        socket.on(eventName, handleNotification);

        return () => {
            socket.off(eventName, handleNotification);
        };
    }, [socket, user, dispatch]);

    const [swipeData, setSwipeData] = useState({ id: null, startX: 0, currentX: 0, isSwiping: false, isLocked: false });

    const userImageStyle = {
        height: '90px',
        width: '90px',
        objectFit: 'cover',
        borderRadius: '50%',
    };

    const mappedNotifications = notifications.map((notif, index) => {
        let name = "User";
        let task = "";
        if (notif.title) {
            name = notif.title;
        }
        if (notif.message) {
            task = notif.message;
        } else if (notif.content) {
            task = typeof notif.content === 'string' ? notif.content : JSON.stringify(notif.content);
        }

        const user = users.find(u => u.id === notif.author_id);

        let picture = defaultPictures[index % defaultPictures.length];
        if (user && user.profile_picture) {
            if (user.profile_picture.startsWith("http")) {
                picture = user.profile_picture;
            } else {
                picture = `${getAPIbaseURL()}${user.profile_picture}`;
            }
        }

        return {
            id: notif.id,
            name,
            task,
            picture,
            isNew: notif.isNew,
        };
    });

    const handleTouchStart = (index, e) => {
        if (!mappedNotifications[index].isNew) {
            setSwipeData({
                id: index,
                startX: e.touches[0].clientX,
                currentX: e.touches[0].clientX,
                isSwiping: true,
                isLocked: false,
            });
        }
    };

    const handleTouchMove = (index, e) => {
        if (!mappedNotifications[index].isNew && swipeData.isSwiping && swipeData.id === index && !swipeData.isLocked) {
            const deltaX = e.touches[0].clientX - swipeData.startX;
            const maxDeltaX = -window.innerWidth * 0.19;

            if (deltaX <= -80) {
                setSwipeData((prev) => ({
                    ...prev,
                    currentX: swipeData.startX - 80,
                    isLocked: true,
                }));
            } else {
                const clampedDeltaX = Math.max(deltaX, maxDeltaX);
                const clampedDeltaXNoRight = Math.min(clampedDeltaX, 0);

                setSwipeData((prev) => ({
                    ...prev,
                    currentX: clampedDeltaXNoRight + swipeData.startX,
                }));
            }
        }
    };

    const handleTouchEnd = (index) => {
        if (!mappedNotifications[index].isNew && swipeData.isSwiping && swipeData.id === index) {
            const deltaX = swipeData.currentX - swipeData.startX;

            if (deltaX <= -80) {
                setSwipeData((prev) => ({
                    ...prev,
                    currentX: swipeData.startX - 80,
                    isSwiping: false,
                    isLocked: true,
                }));
            } else {
                setSwipeData((prev) => ({
                    ...prev,
                    currentX: prev.startX,
                    isSwiping: false,
                    isLocked: false,
                }));
            }
        }
    };

    const handleDeleteRow = (index) => {
        const notifId = mappedNotifications[index].id;
        if (notifId) {
            dispatch(deleteNotification(notifId));
        }
        setSwipeData({ id: null, startX: 0, currentX: 0, isSwiping: false, isLocked: false });
    };

    const handleDeleteAll = () => {
        notifications.forEach(n => {
            if (n.id) {
                dispatch(deleteNotification(n.id));
            }
        });
    };

    const handleReadAll = () => {
        notifications.forEach(n => {
            if (n.id && n.isNew) {
                dispatch(updateNotification({ id: n.id, update: { isNew: false } }));
            }
        });
    };

    const handleCardClick = (index) => {
        const notif = mappedNotifications[index];
        if (notif && notif.id) {
            if (notif.isNew) {
                dispatch(updateNotification({ id: notif.id, update: { isNew: false } }));
                setLastClickedId(notif.id);
                setClickCount(1);
            } else {
                if (lastClickedId === notif.id) {
                    if (clickCount === 1) {
                        navigate('/backoffice?tab=absences');
                        setClickCount(0);
                        setLastClickedId(null);
                    } else {
                        setClickCount(clickCount + 1);
                    }
                } else {
                    setLastClickedId(notif.id);
                    setClickCount(1);
                }
            }
        }
    };

    useEffect(() => {
        const handleContainerTouchStart = (_e) => {
            if (swipeData.isLocked) {
                setSwipeData((prev) => ({
                    ...prev,
                    currentX: prev.startX,
                    isSwiping: false,
                    isLocked: false,
                }));
            }
        };

        const container = containerRef.current;
        if (container) {
            container.addEventListener('touchstart', handleContainerTouchStart);
        }

        return () => {
            if (container) {
                container.removeEventListener('touchstart', handleContainerTouchStart);
            }
        };
    }, [swipeData.isLocked]);

    const newCount = notifications.filter(n => n.isNew).length;

    return (
        <>
            <Container className='h-100'>
                <Row>
                    <Col className='mt-4'>
                        <HeaderMobile
                            showArrow={true}
                            onArrow={() => navigate(-1)}
                            showEdit={false}
                            showDelete={false}
                            onDelete={() => alert("Deleted!")}
                            onEdit={() => alert("Edit!")}
                        >
                            <div className="d-flex justify-content-between align-items-center w-100 px-2">
                                <span>Notificações</span>
                                <Badge bg="danger" className='rounded-circle d-flex justify-content-center align-items-center ms-2' style={{ width: "30px", height: "30px", fontSize: "16px" }} >{newCount}</Badge>
                            </div>
                        </HeaderMobile>

                    </Col>
                </Row>
            </Container>
            <Container>
                <Row className='mt-4 text-white'>
                    <Col className='col-6 d-flex justify-content-start align-items-end'>
                        <b onClick={handleReadAll} style={{ cursor: "pointer" }}>Marcar Tudo Lido</b>
                    </Col>
                    <Col className='col-6 d-flex justify-content-end align-items-end'>
                        <b onClick={() => handleDeleteAll()} style={{ cursor: "pointer" }}>Limpar Tudo</b>
                    </Col>
                </Row>
            </Container>
            <Container ref={containerRef} className='custom-scroll'>
                {mappedNotifications.map((item, index) => (
                    <Row className='position-relative' key={item.id || index}>
                        <div
                            style={{
                                position: "absolute",
                                right: 0,
                                top: 0,
                                bottom: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                paddingRight: "0px",
                            }}
                        >
                            <div onClick={() => handleDeleteRow(index)}>
                                <FaTrash className="fs-2 me-4" />
                            </div>
                        </div>
                        <Card
                            className='rounded-0'
                            style={{
                                backgroundColor: item.isNew ? "#17296B" : "#0E46A3",
                                transform: swipeData.id === index ? `translateX(${swipeData.currentX - swipeData.startX}px)` : "translateX(0)",
                                transition: swipeData.id === index ? "transform 0.3s ease" : "none",
                                borderBottom: '2px solid grey',
                                borderTop: 'none',
                                borderLeft: 'none',
                                borderRight: 'none',
                                position: 'relative',
                                overflow: 'hidden',
                                padding: '0',
                                margin: '0',
                            }}
                            onTouchStart={(e) => handleTouchStart(index, e)}
                            onTouchMove={(e) => handleTouchMove(index, e)}
                            onTouchEnd={() => handleTouchEnd(index)}
                            onClick={() => handleCardClick(index)}
                        >
                            <div style={{ position: 'relative' }}>
                                {item.isNew && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: '8px',
                                            right: '8px',
                                            width: '12px',
                                            height: '12px',
                                            borderRadius: '50%',
                                            backgroundColor: 'rgb(0,178,255)',
                                            boxShadow: '0 0 6px rgba(0,0,0,0.3)',
                                        }}
                                    ></div>
                                )}
                                <Row className='align-items-center'>
                                    <Col className='col-3 d-flex justify-content-center'>
                                        <Image src={item.picture} fluid className='mt-2 mb-2' style={userImageStyle} />
                                    </Col>
                                    <Col className='col-9 d-flex align-items-center'>
                                        <p className='mb-0 text-white'>
                                            <b>{item.name}</b>.<br /><b>{item.task}</b>
                                        </p>
                                    </Col>
                                </Row>
                            </div>
                        </Card>
                    </Row>
                ))}
            </Container>
        </>
    );
}

export default Notifications;
